<template>
  <page-template :is-loading="isLoading" :data-exists="!!logExporters">
    <template #pageHeadline>
      {{ $t('logExporter.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="logExporters"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="log-exporters-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewLogExporter()"
        :show-download-button="false"
        :show-filter-row="true"
        @delete-row-button-clicked="openDeleteLogExporterModal"
      >
        <template #expiredCell="cellProps">
          <div class="expired" v-if="cellProps.row.connection.certificate?.expired">
            {{ $t('logExporter.listPage.certificateExpired') }}
          </div>
        </template>
        <template #activeCell="cellProps">
          <rbz-form-toggle
            v-model:buttonValue="cellProps.row.active"
            @update:button-value="toggleLogExporterActive(cellProps.row.id, $event)"
            :label="$t('logExporter.listPage.active')"
            class="status-input"
            data-qa="status-toggle"
          />
        </template>
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteLogExporterModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteLogExporter"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteLogExporterModal = false"
        @closeModal="showDeleteLogExporterModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedLogExporter.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { ChainData, DocumentName, LogExporter, ServerGroup } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';

import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'LogExportersList',

  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },

  components: {
    RbzFormToggle,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },

  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('logExporter.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getLogExporterPath(item);
          },
        },
        {
          id: 'server-groups',
          title: this.$t('logExporter.listPage.serverGroups'),
          fieldNames: ['server_groups'],
          displayFunction: (item: LogExporter) => {
            const serverGroups = _.filter(this.serverGroupsNames, (serverGroup) => {
              return item.server_groups.ids?.includes(serverGroup[0]);
            });
            const serverGroupsNames = _.map(serverGroups, (serverGroup) => {
              return serverGroup[1];
            });
            if (serverGroupsNames.length === 0) {
              serverGroupsNames.push(this.$t('logExporter.configPage.serverGroupsAll'));
            }
            return serverGroupsNames.join(', ');
          },
          isSearchable: true,
          classes: 'width-240px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'expired',
          fieldNames: ['expired'],
          displaySlotName: 'expiredCell',
          classes: 'width-180px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'status',
          title: this.$t('logExporter.listPage.status'),
          fieldNames: ['active'],
          displaySlotName: 'activeCell',
          classes: 'width-180px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getLogExporterPath,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      logExporters: [] as LogExporter[],
      pageLoadingCounter: null,

      serverGroupsNames: [] as [ServerGroup['id'], ServerGroup['name']][],

      showDeleteLogExporterModal: false,
      selectedLogExporter: null,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Log Exporters List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          const logExportersPromise = this.loadLogExporters();
          const serverGroupsPromise = this.loadServerGroups();
          await Promise.all([logExportersPromise, serverGroupsPromise]);
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        singular: this.$t('logExporter.singular'),
        plural: this.$t('logExporter.plural'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    getNewLogExporter() {
      return this.getNewPath('log-exporters');
    },

    getLogExporterPath(LogExporter: LogExporter) {
      return this.getEditPath('log-exporters', LogExporter.id);
    },

    async deleteLogExporter() {
      this.setPageLoadingStatus(true);
      const logExporterId = this.selectedLogExporter.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/log-exporters/${logExporterId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.logExporters = _.filter(this.logExporters, (LogExporter) => {
          return LogExporter.id !== logExporterId;
        });
      }
      this.showDeleteLogExporterModal = false;
      this.setPageLoadingStatus(false);
    },

    openDeleteLogExporterModal(logExporter: LogExporter) {
      this.showDeleteLogExporterModal = true;
      this.selectedLogExporter = logExporter;
    },

    async loadLogExporters() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/log-exporters`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
        displayServerFailureMessage: true,
      });
      this.logExporters = response?.data?.items || [];
      this.setExpired(this.logExporters);

      this.setPageLoadingStatus(false);
    },

    async loadServerGroups() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.serverGroupsNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },

    async toggleLogExporterActive(id: string, value: boolean) {
      this.setPageLoadingStatus(true);
      const logExporterToSave: LogExporter = _.cloneDeep(_.find(this.logExporters, { id: id }));
      logExporterToSave.active = value;
      if (logExporterToSave.connection.certificate) {
        delete logExporterToSave.connection.certificate['chain_data'];
        delete logExporterToSave.connection.certificate['expired'];
      }
      const successMessage = this.$t('logExporter.listPage.toggleActiveSuccessMessage', {
        name: logExporterToSave.name,
        status: value ? this.$t('activated') : this.$t('disabled'),
      });
      const failureMessage = this.$t('logExporter.listPage.toggleActiveFailureMessage', {
        name: logExporterToSave.name,
      });
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'PUT',
        url: `conf/${this.selectedBranch}/log-exporters/${logExporterToSave.id}`,
        data: logExporterToSave,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      }).then(() => {
        this.loadLogExporters();
      });
      this.setPageLoadingStatus(false);
    },

    setExpired(logExporters: LogExporter[]) {
      const now = Date.now();
      logExporters.forEach((item: LogExporter) => {
        const chainData = item.connection.certificate?.chain_data as ChainData[];
        if (chainData) {
          for (let i = 0; i < chainData.length; i++) {
            if (new Date(chainData[i].exp_date).getTime() < now) {
              item.connection.certificate['expired'] = true;
              break;
            }
          }
        }
      });
    },
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.expired {
  align-items: center;
  background-color: map.get(colors.$colors, 'linen');
  border-radius: 4px;
  border: 1px solid map.get(colors.$colors, 'crimson');
  color: map.get(colors.$colors, 'crimson');
  display: flex;
  height: 24px;
  justify-content: center;
  max-width: fit-content;
  padding: 4px 8px;
}
</style>
